import { Checkbox } from "@blueprintjs/core";
import { FormikProps } from "formik";
import { FGCustomPanel, IDataTableColumn, showError, showSuccess, useApiEffect } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { BreedApi, BreedWorkbookTypeSaveDto, WorkbookTypeApi, WorkbookTypeGridDto } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { SmallFormGenerator, TransfertList } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface IBreedAllowedWorkbookTypeProps {
  editMode: boolean;
  breedId: number;
}

export const BreedAllowedWorkbookTypePage: React.FunctionComponent<IBreedAllowedWorkbookTypeProps> = ({
  editMode,
  breedId,
}) => {
  const breedApi = useApiService(BreedApi);
  const workbookTypeApi = useApiService(WorkbookTypeApi);
  const [saving, setsaving] = React.useState(false);

  const { t } = useTl();

  const [allowedWorkbook, loadingAllowed] = useApiEffect(
    () => breedApi.getAllowedWorkbookType(+breedId, currentLanguage.languageId),
    [breedId]
  );
  const { currentLanguage } = useGlobalData();

  const [workbookTypes, loading] = useApiEffect(() => workbookTypeApi.getAllWorkbookTypes(currentLanguage.languageId));

  const [choosenItems, setChoosenItems] = React.useState([]);

  const updateFormik = React.useCallback(
    (formik: FormikProps<{ choosenItems: WorkbookTypeGridDto[] }>, newData: WorkbookTypeGridDto[]) => {
      formik.setFieldValue("choosenItems", newData);
    },
    []
  );

  React.useEffect(() => {
    !loadingAllowed && setChoosenItems(allowedWorkbook);
  }, [allowedWorkbook, loadingAllowed]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "workbookColor",
        render: (row: WorkbookTypeGridDto) => (
          <Link target="_blank" to={`${ERoutes.workbookType}/${row.workbookTypeId}/detail/view`}>
            {`${row.workbookColor} - ${row.hasPedigree ? t(ETLCodes.WithPedigree) : t(ETLCodes.WithoutPedigree)} (${
              row.description
            })`}
          </Link>
        ),
      },

      {
        header: () => t(ETLCodes.HasPedigree),
        fieldName: "hasPedigree",
        autoFitContent: true,
        alignment: "center",
        render: (row: WorkbookTypeGridDto) => <Checkbox checked={row.hasPedigree} disabled />,
      },
    ],
    [t]
  );
  const SaveSelected = React.useCallback(
    async (formikData: { choosenItems: WorkbookTypeGridDto[] }) => {
      try {
        setsaving(true);
        const res = await breedApi.saveWorkbookTypes(
          BreedWorkbookTypeSaveDto.fromJS({
            breedId: +breedId,
            workbookTypeIds: formikData.choosenItems.map((c) => c.workbookTypeId),
          })
        );
        if (res !== null) {
          showSuccess(t(ETLCodes.SaveSuccess));
        }
      } catch (exception) {
        showError(t(ETLCodes.Error));
      }
      setChoosenItems(formikData.choosenItems);
      setsaving(false);
    },
    [breedApi, breedId, t]
  );
  return (
    <SmallFormGenerator
      initialValues={{ choosenItems: choosenItems }}
      onSubmit={SaveSelected}
      editMode={editMode}
      loading={loading}
      saving={saving}
    >
      <FGCustomPanel>
        {(ctx) => (
          <TransfertList
            allItems={workbookTypes}
            choosenItems={ctx.formik.values?.choosenItems}
            onChoosenItemsChange={(newData: WorkbookTypeGridDto[]) => updateFormik(ctx.formik, newData)}
            columns={columns}
            isLoading={loading}
            keyFieldName="workbookTypeId"
            editMode={editMode}
            sortKey={{ workbookColor: "ASC" }}
          />
        )}
      </FGCustomPanel>
    </SmallFormGenerator>
  );
};
